import React, { useState } from "react";
import { _refusDeVague } from "../../lib/geste-executer";
import { lireLevier } from "../../lib/operateur-leviers";

// CarteGeste — ce que Dimitri lit AVANT de cliquer.
//
// ══ CE QUE LA CARTE DOIT DIRE, ET DANS CET ORDRE ════════════════════════════
//
//   1. CE QU'IL A VU        — le fait, en base, qui a motivé la proposition
//   2. CE QUI VA SE PASSER  — le geste, nommé par le registre (jamais par le modèle)
//   3. CE QUE ÇA VA CHANGER — l'écran où Dimitri le constatera
//   4. LE MOTIF             — quand le levier en exige un : figé, composé de
//                             faits vérifiés, NON éditable
//
// L'ordre n'est pas cosmétique. Dimitri approuve ce qu'il LIT : si le geste
// est annoncé avant le fait, il approuve une action sans sa raison.
//
// ══ LE MOTIF N'EST PAS ÉDITABLE, ET C'EST UNE DÉCISION ══════════════════════
//
//   Dimitri, 2026-09-08 : « le motif figé, tiré des faits vérifiés en base.
//   Jamais rédigé par le modèle. Un acte approuvé d'un clic, c'est le moment
//   où je relis le moins. Une IA qui écrit la justification d'un mouvement
//   d'argent que je signe sans relire, c'est ce qui se retourne contre moi le
//   jour d'un litige. »
//
// Il est donc AFFICHÉ (Dimitri doit voir ce qu'il signe) mais pas modifiable.
// « Si je veux ajouter quelque chose, je le ferai dans la fiche. »
//
// ⚠️ NE PAS transformer ce bloc en champ de saisie « pour rendre service ».

// ══ UN GESTE NON OUVERT A UN BOUTON GRIS, ET UNE PHRASE ═════════════════════
//
// Défaut vécu, puis DÉPLACÉ D'UN CRAN (revue du 2026-09-28) : le serveur
// marquait bien la carte fermée (`geste_ouvert:false` + `geste_ferme`), et ce
// composant ne lisait ni l'un ni l'autre — bouton navy « Le faire »,
// indiscernable d'un geste ouvert. Dimitri cliquait, le refus tombait APRÈS,
// en petit, sous la carte. Le 409 était devenu un refus client ; le bouton,
// lui, promettait toujours.
//
// ⚠️ MÊME JUGE QUE LA CARTE DE DEMANDE ET QUE L'EXÉCUTEUR (`_refusDeVague`) —
// jamais une règle à côté : une seconde lecture du marqueur diverge au premier
// correctif appliqué d'un seul côté.
export default function CarteGeste({ proposition, etat, onExecuter }) {
  const [deploye, setDeploye] = useState(false);
  const p = proposition || {};
  const enCours = etat?.phase === "envoi";
  const fait = etat?.phase === "fait";
  // Le motif du serveur d'abord (ses mots, sa vérité), le juge client ensuite
  // — qui dit la même chose, et dit en plus « rechargez » quand la carte ne
  // porte aucun verdict (vieil onglet).
  const levierRegistre = lireLevier(p.levier);
  const refusOuverture =
    (p.geste_ferme && p.geste_ferme.message ? p.geste_ferme : null) ||
    (levierRegistre
      ? _refusDeVague(
          { levier: p.levier, geste_ouvert: p.geste_ouvert },
          levierRegistre,
        )
      : // ⚠️ LEVIER ABSENT DU REGISTRE : fail-closed, jamais un bouton actif
        // par défaut. Le serveur pose normalement `levier_retire` et la carte
        // n'arrive même pas jusqu'ici ; si les deux manquent, le silence ne
        // doit pas valoir autorisation.
        {
          message:
            "Cette action n'existe plus dans l'application — rien ne peut partir d'ici. Rien n'a été fait.",
        });
  const nonOuvert = !!refusOuverture && !fait;

  return (
    <div
      data-testid={`geste-${p.id}`}
      style={{
        border: "1px solid var(--bord)",
        borderRadius: 12,
        padding: "12px 13px",
        margin: "0 0 10px",
        background: "var(--fond)",
      }}
    >
      {/* 1 · CE QU'IL A VU — le fait d'abord, jamais le geste. */}
      {p.vu ? (
        <p
          data-testid="geste-vu"
          style={{ margin: "0 0 7px", fontSize: 12.5, color: "var(--navy-d)" }}
        >
          {p.vu}
        </p>
      ) : null}

      {/* 2 · CE QUI VA SE PASSER — le titre vient du REGISTRE. */}
      <p
        data-testid="geste-titre"
        style={{ margin: "0 0 4px", fontSize: 13.5, fontWeight: 600 }}
      >
        {p.titre}
      </p>

      {/* 3 · CE QUE ÇA VA CHANGER — la garde E2, rendue lisible. */}
      {p.constatable ? (
        <p
          data-testid="geste-constatable"
          style={{ margin: "0 0 8px", fontSize: 11.5, color: "var(--gris)" }}
        >
          Après votre clic : {p.constatable}.
        </p>
      ) : null}

      {/* 4 · LE MOTIF — affiché, jamais éditable (voir en-tête). */}
      {p.motif ? (
        <div
          data-testid="geste-motif"
          style={{
            margin: "0 0 9px",
            padding: "7px 9px",
            borderRadius: 8,
            background: "var(--fond-2)",
            fontSize: 11.5,
            color: "var(--gris-d)",
          }}
        >
          <span style={{ opacity: 0.7 }}>Motif enregistré : </span>
          {p.motif}
        </div>
      ) : null}

      {/* Le détail : ce qui part exactement. Replié — la carte doit se lire
          d'un coup d'œil, mais rien ne doit être caché à qui veut regarder. */}
      <button
        type="button"
        data-testid="geste-detail-bascule"
        aria-expanded={deploye}
        onClick={() => setDeploye((v) => !v)}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          font: "inherit",
          fontSize: 11,
          color: "var(--gris)",
        }}
      >
        {deploye ? "Masquer le détail" : "Voir exactement ce qui part"}
      </button>
      {deploye ? (
        <pre
          data-testid="geste-detail"
          style={{
            margin: "6px 0 0",
            padding: "7px 9px",
            borderRadius: 8,
            overflowX: "auto",
            background: "var(--fond-2)",
            fontSize: 10.5,
            color: "var(--gris-d)",
          }}
        >
          {p.levier}
          {"\n"}
          {JSON.stringify(p.args || {}, null, 2)}
        </pre>
      ) : null}

      {etat?.erreur ? (
        <p
          data-testid="geste-erreur"
          style={{ margin: "8px 0 0", fontSize: 11.5, color: "var(--terra)" }}
        >
          {etat.erreur}
        </p>
      ) : null}

      {/* Le motif, AU-DESSUS du bouton gris : on lit pourquoi avant de voir
          qu'on ne peut pas. Une décision en attente reste entière à l'écran —
          le fait, le titre, le détail —, elle ne devient qu'inerte. */}
      {nonOuvert ? (
        <p
          data-testid="geste-non-ouvert"
          style={{
            margin: "8px 0 0",
            fontSize: 11.5,
            color: "#8f2f1d",
            fontWeight: 600,
          }}
        >
          ⛔ {refusOuverture.message}
        </p>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button
          type="button"
          data-testid="geste-faire"
          className="fbz-press"
          disabled={enCours || fait || nonOuvert}
          onClick={() => onExecuter && onExecuter(p)}
          style={{
            minHeight: 32,
            padding: "0 14px",
            borderRadius: 16,
            border: "none",
            background: fait
              ? "var(--vert)"
              : nonOuvert
                ? "var(--gris)"
                : "var(--navy)",
            color: "#fff",
            fontSize: 12,
            fontWeight: 600,
            cursor: enCours || fait || nonOuvert ? "default" : "pointer",
            fontFamily: "inherit",
            opacity: enCours || nonOuvert ? 0.6 : 1,
          }}
        >
          {fait ? "Fait" : enCours ? "En cours…" : "Le faire"}
        </button>
      </div>
    </div>
  );
}
